import BaseController from "./BaseController";
import Fragment from "sap/ui/core/Fragment";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import type Button from "sap/m/Button";
import type Dialog from "sap/m/Dialog";
import type ListItemBase from "sap/m/ListItemBase";
import type { SearchField$LiveChangeEvent, SearchField$SearchEvent } from "sap/m/SearchField";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";

export interface PromoBannerListItem {
	id: string;
	slug: string;
	title: string;
	category: string | null;
	language: string | null;
	status: string;
	visibleNow: boolean;
	mediaUrl: string | null;
	displayOrder: number;
	publishFrom: string | null;
	publishUntil: string | null;
	impressions: number;
	clicks: number;
	distinctEnvironments: number;
	lastClickAt: string | null;
}

/** A row as the table binds it: the server fields plus the one ratio the panel derives. */
interface PromoBannerRow extends PromoBannerListItem {
	ctr: string;
}

/**
 * Promotional banners shown in the AppEngine App Store (card 34), gated on promotions.read - a code
 * no organization outside CompuTec holds.
 *
 * Plain JSONModel + getAuthFetch, like LrDeadLetters: promotions are a global resource with no OData
 * entity, read through a REST route.
 */
export default class Promotions extends BaseController {
	private withdrawDialog?: Dialog;
	private allItems: PromoBannerRow[] = [];
	private query = "";

	public onInit(): void {
		const model = new JSONModel({ loaded: false, items: [], statusFilter: "ALL" });
		this.getView()?.setModel(model, "promotions");
		this.getView()?.setModel(new JSONModel({ reason: "", bannerId: "" }), "withdrawPromo");
		this.getRouter().getRoute("promotions")?.attachPatternMatched(() => { void this.loadItems(); });
	}

	public onRefresh(): void {
		void this.loadItems();
	}

	public onSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		this.query = (params.query ?? params.newValue ?? "").trim().toLowerCase();
		this.applyFilter();
	}

	public onStatusSelect(): void {
		this.applyFilter();
	}

	public onAddBanner(): void {
		this.getRouter().navTo("promoBannerDetail", { bannerId: "new" });
	}

	public onOpenBanner(event: { getSource(): ListItemBase }): void {
		const row = this.rowOf(event.getSource());
		if (row) {
			this.getRouter().navTo("promoBannerDetail", { bannerId: row.id });
		}
	}

	public async onPublish(event: { getSource(): Button }): Promise<void> {
		const row = this.rowOf(event.getSource());
		if (!row) {
			return;
		}
		await this.post(`/api/v1/promo/banners/${row.id}/publish`, "promotions.published");
	}

	public async onWithdraw(event: { getSource(): Button }): Promise<void> {
		const row = this.rowOf(event.getSource());
		if (!row) {
			return;
		}

		// The command requires a reason: it is written into the closed publication window and is the
		// only answer the history table can later give to "why did this come down".
		const state = this.getView()?.getModel("withdrawPromo") as JSONModel;
		state.setProperty("/reason", "");
		state.setProperty("/bannerId", row.id);

		if (!this.withdrawDialog) {
			this.withdrawDialog = await Fragment.load({
				id: this.getView()?.getId(),
				name: "computec.b2b.portal.view.WithdrawPromoDialog",
				controller: this
			}) as Dialog;
			this.getView()?.addDependent(this.withdrawDialog);
		}
		this.withdrawDialog.open();
	}

	public async onWithdrawSubmit(): Promise<void> {
		const state = this.getView()?.getModel("withdrawPromo") as JSONModel;
		const bannerId = state.getProperty("/bannerId") as string;
		const reason = state.getProperty("/reason") as string;

		this.withdrawDialog?.close();
		await this.post(`/api/v1/promo/banners/${bannerId}/withdraw`, "promotions.withdrawn", { reason });
	}

	public onWithdrawCancel(): void {
		this.withdrawDialog?.close();
	}

	private async post(url: string, successKey: string, body?: object): Promise<void> {
		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.fetch(url, body
				? {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(body)
				}
				: { method: "POST" });
			MessageToast.show(bundle.getText(successKey) ?? "Done");
			await this.loadItems();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("promotions.actionFailure") ?? "Failed"));
		}
	}

	private async loadItems(): Promise<void> {
		const model = this.getView()?.getModel("promotions") as JSONModel;
		model.setProperty("/loaded", false);
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const items = await fetcher.json<PromoBannerListItem[]>("/api/v1/promo/banners");
			this.allItems = (Array.isArray(items) ? items : []).map(item => ({
				...item,
				ctr: Promotions.clickThroughRate(item)
			}));
			this.applyFilter();
		} catch (e) {
			const err = e as ResponseError;
			const bundle = await this.getResourceBundle();
			this.allItems = [];
			model.setProperty("/items", []);
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("promotions.loadFailure") ?? "Failed"));
		} finally {
			model.setProperty("/loaded", true);
		}
	}

	/**
	 * Impressions of zero render as "-" rather than 0%: nobody has seen the banner yet, which is a
	 * different statement from "it was seen and never clicked".
	 */
	private static clickThroughRate(item: PromoBannerListItem): string {
		return item.impressions > 0
			? `${((item.clicks / item.impressions) * 100).toFixed(1)}%`
			: "-";
	}

	/**
	 * Filtered in memory rather than through a query parameter: the whole list is one unpaged read
	 * of a catalogue measured in dozens of rows, and a round trip per keystroke would buy nothing.
	 * Search and status narrow together - picking a status must not drop the typed query.
	 */
	private applyFilter(): void {
		const model = this.getView()?.getModel("promotions") as JSONModel;
		const status = (model.getProperty("/statusFilter") as string) ?? "ALL";

		const matches = (item: PromoBannerRow): boolean => {
			if (status !== "ALL" && item.status !== status) {
				return false;
			}
			if (this.query.length === 0) {
				return true;
			}
			return item.title.toLowerCase().includes(this.query)
				|| item.slug.toLowerCase().includes(this.query)
				|| (item.category ?? "").toLowerCase().includes(this.query);
		};

		model.setProperty("/items", this.allItems.filter(matches));
	}

	private rowOf(source: Button | ListItemBase): PromoBannerRow | undefined {
		const ctx = source.getBindingContext("promotions");
		return ctx ? ctx.getObject() as PromoBannerRow : undefined;
	}
}
